import { memo, useState, useRef, useCallback, useMemo, useEffect } from "react";
import styled from "styled-components";
import { FaXmark } from "react-icons/fa6";
import { useAtom, useAtomValue } from 'jotai';
import { showGeocollectPanelAtom } from "../../stores/mapStore";
import { currentProjectAtom } from '~/stores/ProjectStore';
import { Tooltip, Tabs } from "antd";
import MapUtils from "../../utils/MapUtils";
import { PiMagnifyingGlass } from "react-icons/pi";
import { PiDownload, PiCaretUpDown, PiCamera, PiImages } from "react-icons/pi";
import { IoIosRefresh } from "react-icons/io";
import { HiOutlineCollection } from "react-icons/hi";
import { HiOutlineClipboardList, HiOutlineDatabase, HiOutlineUsers, HiOutlineEye, HiOutlineChartBar } from "react-icons/hi";
import CollectDataTab from "./tabs/CollectDataTab";
import CollectFormsTab from "./tabs/CollectFormsTab";
import MembersTab from "./tabs/MembersTab";
import LayerTab from "./tabs/LayerTab";
import DashboardTab from "./tabs/DashboardTab";
import FreePhotoTab from "./tabs/FreePhotoTab";
import CollectedPhotosTab from "./tabs/CollectedPhotosTab";
import ErrorBoundary from '~/components/common/ErrorBoundary';

const { TabPane } = Tabs;

const GeocollectDatatablePanel = () => {
  const [showGeocollectPanel, setShowGeocollectPanel] = useAtom(showGeocollectPanelAtom);
  const [currentProject] = useAtom(currentProjectAtom);
  const [isMaximized, setIsMaximized] = useState(false);
  const [panelHeight, setPanelHeight] = useState(window.innerHeight * 0.5);
  const [isResizing, setIsResizing] = useState(false);
  const [ghostHeight, setGhostHeight] = useState<number>(window.innerHeight * 0.5);
  const ghostHeightRef = useRef<number>(window.innerHeight * 0.5);
  const panelHeightRef = useRef<number>(window.innerHeight * 0.5);
  const dragRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    panelHeightRef.current = panelHeight;
  }, [panelHeight]);

  // Cleanup when unmounting to guarantee map resizes to full view
  useEffect(() => {
    return () => {
      MapUtils.triggerResizeEvent();
    };
  }, []);

  const [selectedFormUuid, setSelectedFormUuid] = useState<string | undefined>();
  const [selectedFormData, setSelectedFormData] = useState<any>(undefined);
  const [activeTab, setActiveTab] = useState<string>('dashboard');

  const handleFormSelect = (formUuid: string, formData?: any) => {
    setSelectedFormUuid(formUuid);
    setSelectedFormData(formData);
    setActiveTab('data'); // Auto switch to data tab when form is selected
  };

  // Calculate available height for table content
  const tableHeight = useMemo(() => {
    if (isMaximized) {
      // When maximized: full height minus header (~45px) minus tabs nav (~32px) minus padding (~16px) minus pagination (~48px)
      return 'calc(100vh - 130px)';
    }
    // When resizable: panel height minus header minus tabs nav minus padding minus pagination
    const availableHeight = panelHeight - 130; // More aggressive calculation for more rows
    return availableHeight > 100 ? `${availableHeight}px` : '100px'; // Minimum height
  }, [isMaximized, panelHeight]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    const startY = e.clientY;
    const startHeight = panelHeightRef.current;
    ghostHeightRef.current = startHeight;
    setGhostHeight(startHeight);

    let animationFrame: number | null = null;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      const deltaY = startY - moveEvent.clientY;
      const newHeight = Math.max(200, Math.min(window.innerHeight * 0.9, startHeight + deltaY));
      ghostHeightRef.current = newHeight;

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
      animationFrame = requestAnimationFrame(() => {
        setGhostHeight(newHeight);
      });
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      setIsResizing(false);
      const finalHeight = ghostHeightRef.current;
      setPanelHeight(finalHeight);

      // Trigger map resize only once when drag ends
      requestAnimationFrame(() => {
        MapUtils.triggerResizeEvent();
      });
    };

    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, []);

  const handleMaximize = () => {
    setIsMaximized(!isMaximized);
    requestAnimationFrame(() => {
      MapUtils.triggerResizeEvent();
    });
  };

  const handleClose = () => {
    setShowGeocollectPanel(false);
    requestAnimationFrame(() => {
      MapUtils.triggerResizeEvent();
    });
  };

  return (
    <>
      {isResizing && (
        <>
          <UI_DragOverlay />
          <UI_DragGhostBar style={{ bottom: `${ghostHeight}px` }}>
            <UI_DragGhostBadge>{Math.round(ghostHeight)}px</UI_DragGhostBadge>
          </UI_DragGhostBar>
        </>
      )}
      <UI_Wrapper style={{ height: isMaximized ? '100%' : `${panelHeight}px` }}>
        <UI_DragHandle
          ref={dragRef}
          onMouseDown={handleMouseDown}
          style={{ cursor: 'ns-resize' }}
        >
          <UI_DragIndicator />
        </UI_DragHandle>

    <ErrorBoundary compact title="Lỗi hiển thị bảng dữ liệu Geocollect">
      <UI_Content>
        <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        tabPosition="left"
        destroyInactiveTabPane={true}
        tabBarExtraContent={{
          left: (
            <UI_Header_Left>
              <UI_Header_Icon>
                <HiOutlineCollection />
              </UI_Header_Icon>
              <UI_Header_Title>
                <UI_Header_MainTitle>Geocollect</UI_Header_MainTitle>
                <UI_Header_Subtitle>Dữ liệu thu thập</UI_Header_Subtitle>
              </UI_Header_Title>
            </UI_Header_Left>
          )
        }}
      >
        <TabPane
          tab={
            <UI_TabContent>
              <HiOutlineChartBar />
              <UI_TabText>Dashboard</UI_TabText>
            </UI_TabContent>
          }
          key="dashboard"
        >
          <DashboardTab
            tableHeight={tableHeight}
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
            projectId={currentProject?.id}
            onFormSelect={handleFormSelect}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <HiOutlineDatabase />
              <UI_TabText>Dữ liệu thu thập</UI_TabText>
            </UI_TabContent>
          }
          key="data"
        >
          <CollectDataTab
            collectFormUuid={selectedFormUuid}
            tableHeight={tableHeight}
            onMaximize={handleMaximize}
            onClose={handleClose}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <HiOutlineClipboardList />
              <UI_TabText>Biểu mẫu</UI_TabText>
            </UI_TabContent>
          }
          key="forms"
        >
          <CollectFormsTab
            onFormSelect={handleFormSelect}
            tableHeight={tableHeight}
            selectedFormUuid={selectedFormUuid}
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <PiImages />
              <UI_TabText>Ảnh thu thập</UI_TabText>
            </UI_TabContent>
          }
          key="collectedPhotos"
        >
          <CollectedPhotosTab
            tableHeight={tableHeight}
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <PiCamera />
              <UI_TabText>Ảnh tự do</UI_TabText>
            </UI_TabContent>
          }
          key="freePhotos"
        >
          <FreePhotoTab
            tableHeight={tableHeight}
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <HiOutlineUsers />
              <UI_TabText>Thành viên</UI_TabText>
            </UI_TabContent>
          }
          key="members"
        >
          <MembersTab
            tableHeight={tableHeight}
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
          />
        </TabPane>
        <TabPane
          tab={
            <UI_TabContent>
              <HiOutlineEye />
              <UI_TabText>Hiển thị</UI_TabText>
            </UI_TabContent>
          }
          key="layer"
        >
          <LayerTab
            onMaximize={handleMaximize}
            onClose={handleClose}
            isMaximized={isMaximized}
          />
        </TabPane>
      </Tabs>
    </UI_Content>
    </ErrorBoundary>
  </UI_Wrapper>
  </>
  );
}

export default memo(GeocollectDatatablePanel);

const UI_TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
`

const UI_TabText = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`


const UI_Header_Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  margin-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  width: 100%;

  @media (max-width: 768px) {
    padding: 6px 4px;
    gap: 4px;
  }
`

const UI_Header_Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 6px;
  color: white;
  font-size: 14px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
`

const UI_Header_Title = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const UI_Header_MainTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.2;

  @media (max-width: 768px) {
    display: none;
  }
`

const UI_Header_Subtitle = styled.div`
  font-weight: 400;
  font-size: 11px;
  color: #64748b;
  line-height: 1.2;

  @media (max-width: 768px) {
    display: none;
  }
`

const UI_DragHandle = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 6px;
  cursor: ns-resize;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`



const UI_Wrapper = styled.div`
  width: 100%;
  height: 50vh;
  z-index: 1000;
  background-color: rgba(255,255,255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadows-paneShadow);
  position: relative;

  @media (max-width: 768px) {
    height: 60vh;
    background-color: rgba(255,255,255, 0.95);
  }

  @media (max-width: 480px) {
    height: 70vh;
    background-color: white;
    backdrop-filter: none;
  }
`

const UI_DragIndicator = styled.div`
  width: 40px;
  height: 3px;
  background: #ccc;
  border-radius: 2px;

  ${UI_DragHandle}:hover & {
    background: #999;
  }
`

const UI_DragOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;
  cursor: ns-resize;
  user-select: none;
`

const UI_DragGhostBar = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  height: 4px;
  background: #1677ff;
  box-shadow: 0 0 10px rgba(22, 119, 255, 0.75);
  z-index: 100000;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`

const UI_DragGhostBadge = styled.div`
  background: #1677ff;
  color: #ffffff;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  transform: translateY(-50%);
  user-select: none;
`

const UI_Content = styled.div`
  width: 100%;
  height: calc(100% - 6px);
  margin-top: 6px;
  display: flex;
  flex-direction: row;
  background: #ffffff;

  .ant-tabs {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    flex: 1;
  }

  .ant-tabs-nav {
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    background: #ffffff;
    border-right: 1px solid #f1f5f9;
    display: flex;
    flex-direction: column;

    @media (max-width: 768px) {
      width: 60px !important;
      min-width: 60px !important;
    }
  }

  .ant-tabs-nav-list {
    padding: 0 6px 8px 6px;
  }

  .ant-tabs-extra-content {
    order: -1;
  }

  .ant-tabs-content {
    padding: 0;
  }

  .ant-tabs-tab {
    padding: 8px 12px !important;
    margin: 4px 0 !important;
    border-radius: 6px 0 0 6px !important;
    border: none !important;
    background: transparent !important;
    color: #64748b !important;
    font-weight: 500 !important;
    font-size: 12px !important;
    transition: all 0.2s ease !important;
    justify-content: flex-start !important;

    @media (max-width: 768px) {
      padding: 8px 6px !important;
      justify-content: center !important;
      min-width: 36px !important;
    }

    &:hover {
      background: rgba(59, 130, 246, 0.05) !important;
      color: #3b82f6 !important;
    }
  }

  .ant-tabs-tab-active {
    background: linear-gradient(90deg, #f8fafc 0%, #f1f5f9 100%) !important;
    color: #3b82f6 !important;
    border-right: 3px solid #3b82f6 !important;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.1) !important;
  }

  .ant-tabs-ink-bar {
    display: none !important;
  }

  .ant-tabs-content-holder {
    flex: 1;
    overflow: hidden;
    background: #ffffff;
    min-width: 0;
  }

  .ant-tabs-content {
    height: 100%;
    width: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
    width: 100%;
    overflow: auto;
    padding: 0 !important;
    padding-left: 0 !important;
    min-width: 0;
  }

  // Selected form row highlighting
  .selected-form-row {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.05) 100%) !important;
    border-left: 3px solid #3b82f6 !important;
  }

  .selected-form-row:hover {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(29, 78, 216, 0.08) 100%) !important;
  }

  .selected-form-row td {
    border-left: none !important;
  }

  // Modern pagination styling
  .ant-pagination {
    margin: 8px 0 4px 0 !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    gap: 4px !important;
    padding: 6px 12px !important;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    border-radius: 6px !important;
    border: 1px solid #e2e8f0 !important;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05) !important;
  }

  .ant-pagination-item {
    border: 1px solid #e2e8f0 !important;
    border-radius: 4px !important;
    background: white !important;
    transition: all 0.2s ease !important;
    min-width: 28px !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 1px !important;
    font-size: 12px !important;
  }

  .ant-pagination-item:hover {
    border-color: #3b82f6 !important;
    background: rgba(59, 130, 246, 0.05) !important;
  }

  .ant-pagination-item-active {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
    border-color: #3b82f6 !important;
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3) !important;
  }

  .ant-pagination-item-active a {
    color: white !important;
    font-weight: 500 !important;
  }

  .ant-pagination-prev,
  .ant-pagination-next {
    border: 1px solid #e2e8f0 !important;
    border-radius: 4px !important;
    background: white !important;
    transition: all 0.2s ease !important;
    min-width: 28px !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
  }

  .ant-pagination-prev:hover,
  .ant-pagination-next:hover {
    border-color: #3b82f6 !important;
    background: rgba(59, 130, 246, 0.05) !important;
    color: #3b82f6 !important;
  }

  .ant-pagination-options {
    margin-left: 8px !important;
  }

  .ant-pagination-options-size-changer {
    border-radius: 4px !important;
    border: 1px solid #e2e8f0 !important;
  }

  .ant-pagination-options-quick-jumper input {
    border-radius: 4px !important;
    border: 1px solid #e2e8f0 !important;
    min-width: 40px !important;
    height: 28px !important;
    font-size: 12px !important;
  }

  .ant-pagination-total-text {
    color: #64748b !important;
    font-size: 11px !important;
    font-weight: 500 !important;
    margin-right: 8px !important;

    @media (max-width: 768px) {
      display: none !important;
    }
  }

`